// Portfolio copy lives here so the components only handle layout.

export const profile = {
  name: "Raed Altaki",
  title: "Senior Backend Software Engineer",
  focus: "C# / .NET, REST APIs, SQL",
  location: "Mississauga, ON, Canada",
  email: "raed.simon@gmail.com",
  linkedin: "https://www.linkedin.com/in/raed-altaki/",
  github: "https://github.com/raedaltaki",
};

export const summary = [
  "I'm a backend software engineer with 5+ years building and maintaining production systems in C# and .NET. Most of my work is enterprise software: API integrations, sync platforms, and backend services running in real customer environments.",
  "I own things end to end, from design and implementation through code review and production support. Right now I'm leading the Exchange connector's migration from EWS to Microsoft Graph API at Riva, and working on platform reliability and modernization.",
];

export const talkingPoints = [
  "Delta queries and delta token handling in Microsoft Graph API",
  "changeHash fingerprinting to detect real changes and deletions in recurring calendar events",
  "Handling infinite recurring series inside a rolling sync window",
  "Throttling and retry handling, pagination, and PATCH-based writeback",
  "Self-healing reconciliation jobs that fix data on a schedule instead of by hand",
  "Root-cause debugging with Splunk log analysis and Fiddler trace captures",
];

export const experience = [
  {
    role: "Backend Software Engineer",
    company: "Riva International, Inc.",
    place: "Remote",
    dates: "Sep 2021 - Present",
    intro:
      "Riva builds a sync engine that keeps data in step between enterprise systems, mainly Salesforce CRM and Exchange (EWS and Microsoft Graph API). I started on the core sync engine team, moved to first-line production incident response as a Professional Services Engineer, and am now Feature Technical Lead for Graph connector work.",
    highlights: [
      "Leading the Exchange connector migration from EWS to Microsoft Graph API for the calendar and task modules, covering architecture, implementation, and rollout.",
      "Designed Graph calendar sync: delta queries, changeHash fingerprinting for occurrence-level changes and deletions, metadata tagging for infinite recurring series, and a rolling 30-day-past / 90-day-future window.",
      "Built an attendee-rematching job that re-processes unmatched attendees weekly against new CRM contacts. It is now used across all connectors.",
      "Improved backend API response time by 30%+ by reworking caching and cutting unnecessary downstream calls.",
      "Cut test suite runtime by 80% by introducing parallel test execution.",
      "Designed a cloud migration for an enterprise client moving from on-premises to a cloud sync service, keeping sync running against their local database throughout.",
      "Tracked down a Salesforce writeback loop through Splunk log analysis and shipped a fix that detects and suppresses self-generated writebacks.",
      "Found a server-side Graph delta sync timeout with Fiddler traces, reported it to Microsoft, and shipped a client-side continuation-token resume in the meantime.",
      "Wrote a PowerShell tool that checks a customer's EWS setup against Graph feature support and produces a migration-readiness report, replacing a manual assessment.",
    ],
  },
  {
    role: "Co-op Web Developer",
    company: "RTS Consulting - Automation",
    place: "Toronto, ON",
    dates: "Apr 2021 - Aug 2021",
    highlights: [
      "Built a web app (JavaScript, XML, XSLT) that generates DDL SQL scripts from structured XML input, replacing a manual process.",
      "Developed manufacturing performance reports in SAP MII with JavaScript, SQL, XML, and XSLT.",
    ],
  },
  {
    role: "IT Support Engineer",
    company: "Consolidated Contractors Company (CCC)",
    place: "Kuwait",
    dates: "Jun 2014 - Jun 2020",
    highlights: [
      "Supported 100+ users across hardware, software, networking, and infrastructure, and set up office and site infrastructure across multiple locations.",
      "This is where I learned production support, incident handling, and root cause analysis under real pressure.",
    ],
  },
];

export const skills = [
  {
    group: "Languages & Frameworks",
    items: ["C#", ".NET / .NET Core", "ASP.NET", "Web API", "REST APIs", "Entity Framework", "LINQ", "MVC", "OOP"],
  },
  {
    group: "Cloud & Integrations",
    items: ["Microsoft Graph API", "Exchange Web Services (EWS)", "Azure", "TeamCity", "CI/CD Pipelines", "Cloud Migrations"],
  },
  {
    group: "Data",
    items: ["SQL Server", "MySQL", "SQLite", "MongoDB", "NoSQL", "Query Optimization"],
  },
  {
    group: "Testing & Quality",
    items: ["Unit Testing", "Integration Testing", "TDD", "Parallel Test Execution", "Code Reviews", "Automated Testing"],
  },
  {
    group: "Tools & Practices",
    items: ["Git", "SVN", "Splunk", "Agile / Scrum", "Root Cause Analysis", "Performance Optimization", "Production Debugging"],
  },
  {
    group: "Frontend (supporting)",
    items: ["JavaScript", "React.js", "Vue.js", "HTML5", "CSS3", "XML / XSLT", "JSON"],
  },
];

export const education = [
  {
    name: "Certificate in Coding and Web Development",
    school: "University of Toronto",
    year: "2021",
  },
  {
    name: "Bachelor of Science in Computer Engineering",
    school: "Jordan University of Science and Technology",
    year: "2014",
  },
];

export const featuredProjects = [
  {
    name: "MongoWeb",
    tagline: ".NET 8 microservices",
    description:
      "A microservices solution split into independent services (Auth, Product, Coupon, ShoppingCart, Order, Email, Reward) behind an API gateway, with a shared message bus for communication between services and a web frontend.",
    tech: ["C#", ".NET 8", "Microservices", "API Gateway", "Message Bus"],
    github: "https://github.com/raedaltaki/MongoWeb",
  },
  {
    name: "music",
    tagline: "Vue.js frontend",
    description:
      "A Vue.js app built with Tailwind, with Jest unit tests and Cypress end-to-end tests.",
    tech: ["Vue.js", "Tailwind", "Jest", "Cypress"],
    github: "https://github.com/raedaltaki/music",
  },
];

export const currently =
  "Leading the EWS to Microsoft Graph API migration at Riva";

export const metrics = [
  { value: 5, suffix: "+", label: "years building production C# / .NET systems" },
  { value: 30, suffix: "%+", label: "faster backend API responses after a caching rework" },
  { value: 80, suffix: "%", label: "shorter test suite runtime with parallel execution" },
  { value: 5, suffix: "", label: "certifications across Azure, DevOps, Splunk, SQL, and PMP" },
];

export const rivaPath = [
  { team: "Core sync engine", detail: "Sync engine and connectors" },
  { team: "Professional Services", detail: "First-line production incident response" },
  { team: "Feature Technical Lead", detail: "Microsoft Graph connector" },
];

export const certifications = [
  {
    name: "Splunk Core Certified Power User",
    year: "2024",
    badge: "https://images.credly.com/size/220x220/images/e3d0d18a-edd0-49b3-bad9-f09fffaa29e3/20-14376-SPLK-Certification-Badge-Youracclaim.com-101_Splunk-Core-Certified-Power-User.png",
    file: "20240504-SPLK-1002 Certificate.pdf",
  },
  {
    name: "Microsoft Certified: DevOps Engineer Expert",
    year: "2023",
    badge: "https://images.credly.com/size/220x220/images/c3ab66f8-5d59-4afa-a6c2-0ba30a1989ca/CERT-Expert-DevOps-Engineer-600x600.png",
    file: "20250524-AZ400.pdf",
  },
  {
    name: "Microsoft Certified: Azure Developer Associate",
    year: "2022",
    badge: "https://images.credly.com/size/220x220/images/63316b60-f62d-4e51-aacc-c23cb850089c/azure-developer-associate-600x600.png",
    file: "20251005-AZ204.pdf",
  },
  {
    name: "Oracle Database SQL Certified Associate (OCA)",
    year: "2020",
    badge: "https://images.credly.com/size/220x220/images/13a2043a-12e9-4e5d-aed0-6083534b84fe/07_SQL_Associate__1_.png",
    file: "20200721-Oracle SQL Certificate.pdf",
  },
  {
    name: "Project Management Professional (PMP)",
    year: "2020",
    badge: "https://images.credly.com/size/220x220/images/260e36dc-d100-45c3-852f-9d8063fa71e6/pmp-600px.png",
    file: "20290527-PMP Certfication.pdf",
  },
];

export const earlierProjects = [
  { name: "Shop-Shop", tech: "MERN, GraphQL, Stripe", github: "https://github.com/raedaltaki/shop-shop" },
  { name: "CurrenSeed", tech: "MERN, GraphQL, Chakra UI", github: "https://github.com/DJ-KARMA/currenseed/" },
  { name: "Truevia Game", tech: "Node.js, Express, MySQL, Sequelize, Jest", github: "https://github.com/Group3bootcamp/Truevia-Game" },
  { name: "CMS Blog Site", tech: "Node.js, Express, MySQL, Sequelize", github: "https://github.com/raedaltaki/CMS-blog-site" },
  { name: "Budget Tracker", tech: "Node.js, Express, MongoDB, PWA", github: "https://github.com/raedaltaki/Budget-Tracker" },
  { name: "Deep Thoughts", tech: "MERN, GraphQL, JWT", github: "https://github.com/raedaltaki/deep-thoughts" },
  { name: "Google Books Search", tech: "MERN, GraphQL, REST", github: "https://github.com/raedaltaki/googlebooks-app" },
  { name: "Date Concierge", tech: "JavaScript, AJAX, jQuery", github: "https://github.com/raedaltaki/date-concierge" },
];
